import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import TextEditor from '@components/TextEditor';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { Switch } from '@components/Switch';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { CHANNEL } from '@constants/channels';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { MB } from '@constants/file';
import { validateFileSize } from '@helpers/upload-file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { usePermissions, useTranslation, useCouponsTemplates, useLocations } from '@hooks';
import { alertActions } from '@actions';
import { campaignsService } from '@services';
import TemplatePicker from './TemplatePicker';
import WhatsAppTemplateSection from './WhatsAppTemplateSection';

const StyledRadioButtons = styled(RadioButtonsGroup)({
    marginTop: '8px',
    marginBottom: '8px',
    gridColumn: '1 / span 1',
    '@media (max-width: 600px)': {
        gridColumn: '1 / span 2',
    },
});

export const MESSAGE_TYPE = {
    MESSAGE: 1,
    COUPON: 3,
};

const ContentSource = ({ channelType, isView, messageType, setMessageType }) => {
    const [uploading, setUploading] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { hasAccess } = usePermissions();
    const { control, setValue, watch } = useFormContext();
    const { getCouponsOptions } = useCouponsTemplates();
    const { getLocationsOptions, locationsMap } = useLocations();

    const coverPhotoEnabled = watch('coverPhotoEnabled');
    const coverPhotoUrl = watch('coverPhotoUrl');
    const selectedCouponTemplateId = watch('couponTemplateId');

    const aiContextLocationId = useMemo(() => getLocationsOptions()[0]?.id, [locationsMap]);

    const couponsOptions = useMemo(() => getCouponsOptions(), [getCouponsOptions]);

    useEffect(() => {
        if (couponsOptions.length > 0 && selectedCouponTemplateId) {
            if (!couponsOptions.find(s => s.value === selectedCouponTemplateId)) {
                setValue('couponTemplateId', '', { shouldValidate: true });
            }
        }
    }, [selectedCouponTemplateId, couponsOptions]);

    const IS_EMAIL = channelType === CHANNEL.EMAIL;
    const IS_WHATSAPP_BETA = channelType === CHANNEL.WHATSAPP;
    const IS_WHATSAPP_BUSINESS = channelType === CHANNEL.WHATSAPP_BUSINESS;

    const uploadImage = async e => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 15 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await campaignsService.uploadEmailCampaignImage(data);
            setValue('coverPhotoUrl', response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (_) {
            logger.error(`Couldn't upload coverPhotoUrl`);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <>
            {/* <div style={{ gridColumn: '1 / span 1' }}> */}
            <Controller
                control={control}
                name="messageType"
                render={({ field }) => (
                    <StyledRadioButtons
                        id="pl-radiobuttons"
                        value={field.value}
                        onChange={value => {
                            setValue('messageType', value, { shouldDirty: true, shouldValidate: true });
                            setMessageType(value);
                            setValue('whatsAppTemplateId', '');
                            setValue('whatsAppTemplateRegistryId', '');
                            setValue('couponTemplateId', '');
                            setValue('emailTemplateId', '');
                        }}
                        options={[
                            { value: MESSAGE_TYPE.MESSAGE, label: t('common.message', { count: 1 }) },
                            ...(hasAccess('coupons') || messageType === MESSAGE_TYPE.COUPON
                                ? [{ value: MESSAGE_TYPE.COUPON, label: t('common.coupon') }]
                                : []),
                        ]}
                        disabled={isView}
                    />
                )}
            />
            {/* </div> */}
            {IS_EMAIL && (
                <TemplatePicker
                    isView={isView}
                    requiresDynamicLink={messageType === MESSAGE_TYPE.COUPON}
                    requiredLinkType={messageType === MESSAGE_TYPE.COUPON ? 'coupon' : null}
                />
            )}
            {(messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON) && !IS_EMAIL && (
                <>
                    {!IS_WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="message"
                            render={({ field, fieldState: { error }, ref }) => (
                                <div style={{ gridColumn: '1 / span 2', position: 'relative' }}>
                                    <TextEditor
                                        {...field}
                                        ref={ref}
                                        label={t('common.message', { count: 1 })}
                                        limit={channelType === CHANNEL.WHATSAPP ? 1024 : 0}
                                        placeholderContext={PLACEHOLDER_CONTEXT.CAMPAIGN}
                                        initialValue={field.value || ''}
                                        onChange={text =>
                                            setValue('message', parseTipTapJSONToPlainText(text), {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            })
                                        }
                                        editable={!isView}
                                        required
                                        error={error}
                                        helperText={error ? t(error.message, { count: 1024 }) : null}
                                        toolbar={<PostDynamicVariables disabled={isView} />}
                                        aiAssistant={{
                                            additionalOptions: [
                                                {
                                                    text: t('AIAssistant.writeCampaign'),
                                                    type: AITaskType.GenerateCampaignMessage,
                                                    taskType: AI_TASK_TYPE.CONTEXTUAL,
                                                    companyId: aiContextLocationId,
                                                },
                                            ],
                                        }}
                                    />
                                </div>
                            )}
                        />
                    )}
                </>
            )}
            {messageType === MESSAGE_TYPE.COUPON && (
                <Controller
                    control={control}
                    name="couponTemplateId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('couponTemplateId', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={couponsOptions}
                            required
                            disabled={isView}
                            label={t('common.coupon')}
                            fullWidth
                            style={{ gridColumn: '1 / span 2' }}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
            {IS_WHATSAPP_BUSINESS && (
                <WhatsAppTemplateSection
                    isView={isView}
                    requiresLink={messageType === MESSAGE_TYPE.COUPON}
                    isCouponCampaign={messageType === MESSAGE_TYPE.COUPON}
                />
            )}
            {IS_WHATSAPP_BETA ? (
                <>
                    <Controller
                        control={control}
                        name="coverPhotoEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('coverPhotoEnabled', event.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                label={t('CampaignsPage.addImage')}
                                style={{ gridColumn: '1 / span 2' }}
                                disabled={isView}
                            />
                        )}
                    />
                    {coverPhotoEnabled && (
                        <div style={{ gridColumn: '1 / span 2' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'var(--text-secondary)',
                                    display: ' block',
                                    marginBottom: '8px',
                                }}
                            >
                                {t('CampaignsPage.imageFormat')}
                            </Typography>
                            <ImageUploaderSingle
                                extensions=".png, .jpg, .jpeg"
                                logoUrl={coverPhotoUrl}
                                maxSize={5 * MB}
                                disabled={uploading || isView}
                                onDelete={() =>
                                    setValue('coverPhotoUrl', null, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                }
                                uploadLogo={uploadImage}
                                isUploading={uploading}
                            />
                        </div>
                    )}
                </>
            ) : null}
        </>
    );
};

export default ContentSource;
